import validator from "validator";

const passwordOptions = {
  minLength: 8, 
  minLowercase: 1, 
  minUppercase: 1, 
  minNumbers: 1, 
  minSymbols: 1, 
  returnScore: false, 
  pointsPerUnique: 1, 
  pointsPerRepeat: 0.5, 
  pointsForContainingLower: 10, 
  pointsForContainingUpper: 10, 
  pointsForContainingNumber: 10, 
  pointsForContainingSymbol: 10
}

const passwordErrorMessage = `Password must be atleast ${passwordOptions.minLength} characters in length and must contain 
  ${passwordOptions.minLowercase} lowercase character(s),
  ${passwordOptions.minUppercase} uppercase character(s),
  ${passwordOptions.minNumbers} number(s) and
  ${passwordOptions.minSymbols} special character(s).
`
/*
initialValues: {
      firstName: user.firstName,
      middleName: user.middleName || "",
      lastName: user.lastName,
      email: user.email,
      pin: user.pin,
      isActive: true,
    },
*/

export const LoginFormValidations = {
  username: (value) => validator.isEmail(value) ? null : "Must provide a valid email address.",
  password: (value) => validator.isEmpty(value) ? "Please enter your password." : null
};

export const RegistrationFormValidations = {
  companyName: (value) => {
    if(validator.isEmpty(value)) return "Must provide your company name.";

    return null;
  },
  ein: (value) => {
    if(validator.isEmpty(value)) return "Must provide your company EIN.";
    if(!validator.isNumeric(value, { no_symbols: true })) return "EIN can only contain numbers.";
    if(!validator.isLength(value, { max: 9, min: 9})) return "EIN must be 9 digits in length.";

    return null;
  },
  companyAddressLn1: (value) => {
    if(validator.isEmpty(value)) return "Must provide your company address.";

    return null;
  },
  companyAddressLn2: (value) => {
    // This is an optional field
    return null;
  },
  companyCity: (value) => {
    if(validator.isEmpty(value)) return "Must provide your company city.";

    return null;
  },
  companyState: (value) => {
    if(validator.isEmpty(value)) return "Please provide the two letter state abbreviation. (i.e, OR)";
    if(!validator.isLength(value, { max: 2, min: 2 })) return "Please provide the two letter state abbreviation. (i.e, OR)";

    return null;
  },
  companyZip: (value) => {
    if(validator.isEmpty(value)) return "Must provide your company zip code.";
    if (!validator.isPostalCode(value, 'US') && !validator.isPostalCode(value, 'CA')) return "Must be a valid zip code."

    return null;
  },
  companyRole: (value) => {
    if(validator.isEmpty(value)) return "Must provide your company role.";

    return null;
  },
  contactFirstName: (value) => {
    if(validator.isEmpty(value)) return "Must provide your first name.";

    return null;
  },
  contactLastName: (value) => {
    if(validator.isEmpty(value)) return "Must provide your last name.";

    return null;
  },
  contactTitle: (value) => {
    // This is an optional field
    return null;
  },
  contactEmail: (value) => validator.isEmail(value) ? null : "Must provide a valid email address.",
  contactPhoneNumber: (value) => validator.isMobilePhone(value, 'en-US') ? null : "Must provide a valid phone number.",
  contactPassword: (value) => validator.isStrongPassword(value) ? null : passwordErrorMessage,
  confirmContactPassword: (value, values) => value !== values.password ? "Passwords must match." : null,
  annualContainersSold: (value) => validator.isNumeric(value, { no_symbols: true }),
  annualContainersRedeemed: (value) => validator.isNumeric(value, { no_symbols: true }),
}

export const ResetPasswordRequestFormValidations = {
  email: (value) => validator.isEmail(value) ? null : "Must provide a valid email address.",
};

export const CreateSubmissionFormValidations = {
  productName: (value) => {
    if(validator.isEmpty(value)) return "Please specify the product brand and name (i.e, Arizona Green Tea).";

    return null;
  },
  beverageType: (value) => {
    if(validator.isEmpty(value)) return "Please specify a beverage type (i.e, Soda).";

    return null;
  },
  size: (value) => {
    if(validator.isEmpty(value)) return "Please specify the container size (i.e, 12).";
    if(!validator.isNumeric(value)) return "Size must be a numeric value representing the container ounces.";

    return null;
  },
  materialType: (value) => {
    if(validator.isEmpty(value)) return "Please specify material type (i.e, Aluminum).";

    return null;
  },
  upc: (value) => {
    if(validator.isEmpty(value)) return "Please specify a valid UPC / EAN.";
    if(!validator.isNumeric(value, { no_symbols: true })) return "UPC / EAN must be a numeric value.";
    if(value.length !== 6 && value.length !== 8 && value.length !== 10 && value.length !== 12 && value.length !== 13) {
      return "UPC length must be 6, 8, 10, or 13 characters in length.";
    }

    return null;
  },
  color: (value, values, path) => {
    const idx = path.split('.')[1];

    if(values.entries[idx].materialType === "Plastic" || values.entries[idx].materialType === "Glass") {
      if(validator.isEmpty(value)) return "Please specify a color"
    }

    return null;
  }
}

export const EditSubmissionFormValidations = {
  productName: (value) => {
    if(validator.isEmpty(value)) return "Please specify the product brand and name (i.e, Arizona Green Tea).";

    return null;
  },
  beverageType: (value) => {
    if(validator.isEmpty(value)) return "Please specify a beverage type (i.e, Soda).";

    return null;
  },
  size: (value) => {
    if(validator.isEmpty(value)) return "Please specify the container size (i.e, 12).";
    if(!validator.isNumeric(value)) return "Size must be a numeric value representing the container ounces.";

    return null;
  },
  materialType: (value) => {
    if(validator.isEmpty(value)) return "Please specify material type (i.e, Aluminum).";

    return null;
  },
  upc: (value) => {
    if(validator.isEmpty(value)) return "Please specify a valid UPC / EAN.";
    if(!validator.isNumeric(value, { no_symbols: true })) return "UPC / EAN must be a numeric value.";
    if(value.length !== 6 && value.length !== 8 && value.length !== 10 && value.length !== 12 && value.length !== 13) {
      return "UPC length must be 6, 8, 10, or 13 characters in length.";
    }

    return null;
  },
  color: (value, values) => {
    if(values.materialType === "Plastic" || values.materialType === "Glass") {
      if(validator.isEmpty(value || "")) return "Please specify a color"
    }

    return null;
  }
}